import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ModuleTourStep } from '../module-tour.steps';

interface TourHole {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Spotlight over a `[data-tour]` anchor. The page switches the tab before the step input changes,
 * and the measure waits one frame so the anchor exists.
 */
@Component({
  selector: 'app-module-tour',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div class="tour-blocker" [class.dim]="!hole"></div>
    <div
      class="tour-hole"
      *ngIf="hole"
      [style.top.px]="hole.top"
      [style.left.px]="hole.left"
      [style.width.px]="hole.width"
      [style.height.px]="hole.height"></div>
    <aside class="tour-card" role="dialog" [attr.aria-label]="step.titleKey | translate" [style.top.px]="cardTop" [style.left.px]="cardLeft">
      <p class="tour-progress">{{ 'reporting_analytics.tour_progress' | translate: { current: index + 1, total: total } }}</p>
      <h2>{{ step.titleKey | translate }}</h2>
      <p>{{ step.textKey | translate }}</p>
      <div class="tour-actions">
        <button type="button" class="quiet" (click)="skip.emit()">{{ 'reporting_analytics.tour_skip' | translate }}</button>
        <span class="spacer"></span>
        <button type="button" *ngIf="step.offerGuide" (click)="guide.emit()">{{ 'reporting_analytics.tour_guide' | translate }}</button>
        <button type="button" (click)="back.emit()" [disabled]="index === 0">{{ 'reporting_analytics.tour_back' | translate }}</button>
        <button type="button" class="primary" (click)="next.emit()">
          {{ (index === total - 1 ? 'reporting_analytics.tour_done' : 'reporting_analytics.tour_next') | translate }}
        </button>
      </div>
    </aside>
  `,
  styles: [`
    .tour-blocker {
      position: fixed;
      inset: 0;
      z-index: 1000;
      background: transparent;
    }
    .tour-blocker.dim {
      background: rgba(15, 19, 36, 0.55);
    }
    .tour-hole {
      position: fixed;
      z-index: 1001;
      border-radius: 10px;
      pointer-events: none;
      box-shadow: 0 0 0 9999px rgba(15, 19, 36, 0.55);
    }
    .tour-card {
      position: fixed;
      z-index: 1002;
      width: min(380px, calc(100vw - 24px));
      padding: 16px;
      border-radius: 12px;
      background: #fff;
      box-shadow: 0 12px 40px rgba(20, 21, 26, 0.18);
      color: #14151a;
    }
    .tour-progress {
      margin: 0 0 8px;
      color: rgba(15, 19, 36, 0.6);
      font-size: 12px;
    }
    h2 {
      margin: 0 0 8px;
      font-size: 18px;
      line-height: 24px;
    }
    p {
      margin: 0;
      font-size: 14px;
      line-height: 20px;
    }
    .tour-actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      margin-top: 16px;
    }
    .spacer { flex: 1 1 auto; }
    button {
      border: 1px solid #d7dbe3;
      border-radius: 8px;
      background: #fff;
      padding: 6px 10px;
      cursor: pointer;
    }
    button.primary {
      border-color: #2e8f27;
      background: #2e8f27;
      color: #fff;
    }
    button.quiet {
      border-color: transparent;
      background: transparent;
      color: rgba(15, 19, 36, 0.7);
    }
    button:disabled {
      opacity: 0.45;
      cursor: default;
    }
  `]
})
export class ModuleTourComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) step!: ModuleTourStep;
  @Input() index = 0;
  @Input() total = 1;
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @Output() skip = new EventEmitter<void>();
  @Output() guide = new EventEmitter<void>();

  hole: TourHole | null = null;
  cardTop = 24;
  cardLeft = 24;

  private timer = 0;

  ngOnChanges(): void {
    this.schedule();
  }

  ngOnDestroy(): void {
    window.clearTimeout(this.timer);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.measure();
  }

  private schedule(): void {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.measure(), 60);
  }

  private measure(): void {
    const cardWidth = Math.min(380, window.innerWidth - 24);
    if (!this.step?.anchor) {
      this.placeCentered(cardWidth);
      return;
    }
    const anchor = document.querySelector(`[data-tour="${this.step.anchor}"]`);
    if (!(anchor instanceof HTMLElement)) {
      this.placeCentered(cardWidth);
      return;
    }
    anchor.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const rect = anchor.getBoundingClientRect();
    const pad = 6;
    this.hole = {
      top: Math.max(0, rect.top - pad),
      left: Math.max(0, rect.left - pad),
      width: rect.width + pad * 2,
      height: rect.height + pad * 2
    };
    const cardHeight = 240;
    let top = this.hole.top + this.hole.height + 12;
    let left = this.hole.left;
    if (top + cardHeight > window.innerHeight) {
      top = Math.max(12, this.hole.top - cardHeight - 12);
    }
    if (left + cardWidth > window.innerWidth - 12) {
      left = window.innerWidth - cardWidth - 12;
    }
    this.cardTop = Math.max(12, top);
    this.cardLeft = Math.max(12, left);
  }

  private placeCentered(cardWidth: number): void {
    this.hole = null;
    this.cardLeft = Math.max(12, (window.innerWidth - cardWidth) / 2);
    this.cardTop = Math.max(12, (window.innerHeight - 240) / 2);
  }
}
